// =============================================================================
// Shared shape for structured legal-document content (Privacy Policy, Terms
// of Service, Impressum, Partner Terms, Community Guidelines).
//
// English is canonical — see each document's own file. Translations must be
// updated whenever the English source changes; section order and count must
// stay identical across all five languages so LegalScreen.tsx's rendering
// (which just maps `sections`) never has to special-case a language.
//
// `icon` is a Feather icon name (react-native-vector-icons) — icons aren't
// language-specific, so only the EN entry's icon is actually read; kept on
// every language for type-shape simplicity, not duplicated meaning.
// =============================================================================

import type { SupportedLanguage } from '../../i18n';

export interface LegalSection {
  title: string;
  iconName: string;
  paragraphs: string[];
  // Optional bullet list to render after paragraphs[index], keyed by that
  // index — e.g. { 0: [...] } renders a bulleted list between paragraphs[0]
  // and paragraphs[1]. Only Terms of Service Section 14 (Limitation of
  // Liability) currently uses this.
  bulletsAfterParagraph?: Record<number, string[]>;
}

export interface LegalDocumentContent {
  title: string;
  subtitle: string;
  iconName: string;
  sections: LegalSection[];
}

// Legal copy is only exposed in a language after that exact document has an
// approved translation. Keeping non-English entries optional prevents us from
// presenting machine-generated legal terms while preserving the English
// fallback contract.
export type LegalContentByLanguage = { en: LegalDocumentContent }
  & Partial<Record<Exclude<SupportedLanguage, 'en'>, LegalDocumentContent>>;

// Falls back to the English section for any language whose translation is
// missing entirely (should never happen once a document is fully
// translated, but keeps a partial/future translation from rendering blank
// rather than crashing) — mirrors the website i18n's "fall back to English"
// contract.
export function resolveLegalContent(
  byLanguage: LegalContentByLanguage,
  language: SupportedLanguage,
): LegalDocumentContent {
  return byLanguage[language] || byLanguage.en;
}
